import { useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { createPlugin } from '@backstage/core-plugin-api';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRoot,
} from '@backstage/plugin-techdocs-react';

// The TechDocs reader strips every <link> and <style> from mkdocs output except
// Material's own main.css, so mkdocs extra_css cannot brand doc content. Colours
// and heading fonts already flow in from the app theme; this addon adds what the
// reader does not derive from the theme: the body font and table styling.
const BrandStylesAddon = () => {
  const shadowRoot = useShadowRoot();
  const theme = useTheme();

  useEffect(() => {
    if (!shadowRoot) return undefined;
    const { palette } = theme;
    const style = document.createElement('style');
    // Tables follow the brand card grammar: white fill, hairline border, rounded
    // corners, off-white header. Material normally wraps tables in a scroll
    // container via JS, which Backstage strips, so the card is the table itself.
    style.textContent = `
      :host { --md-text-font: "Manrope"; }
      .md-typeset { font-weight: 500; line-height: 1.625; }

      .md-typeset table:not([class]) {
        display: table;
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        border: 1px solid ${palette.divider};
        border-radius: 12px;
        overflow: hidden;
        background: ${palette.background.paper};
        box-shadow: none;
        font-size: 0.875rem;
      }
      .md-typeset table:not([class]) th:first-child { border-top-left-radius: 11px; }
      .md-typeset table:not([class]) th:last-child { border-top-right-radius: 11px; }
      .md-typeset table:not([class]) th {
        padding: 0.75rem 1rem;
        background: ${palette.background.default};
        color: ${palette.text.secondary};
        font-size: 0.6875rem;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        text-align: left;
        vertical-align: bottom;
        border-bottom: 1px solid ${palette.divider};
      }
      .md-typeset table:not([class]) td {
        padding: 0.7rem 1rem;
        line-height: 1.5;
        vertical-align: top;
        border-top: 0;
        border-bottom: 1px solid ${palette.divider};
      }
      .md-typeset table:not([class]) tbody tr:last-child td { border-bottom: 0; }
      .md-typeset table:not([class]) tbody tr:hover { background: ${palette.action.hover}; }
      .md-typeset table:not([class]) td:first-child {
        font-weight: 600;
        color: ${palette.text.primary};
      }
      .md-typeset table:not([class]) th[align="right"],
      .md-typeset table:not([class]) td[align="right"] {
        font-variant-numeric: tabular-nums;
      }
    `;
    shadowRoot.appendChild(style);
    return () => style.remove();
  }, [shadowRoot, theme]);

  return null;
};

const qedBrandPlugin = createPlugin({ id: 'qed-brand' });

export const BrandStyles = qedBrandPlugin.provide(
  createTechDocsAddonExtension({
    name: 'BrandStyles',
    location: TechDocsAddonLocations.Content,
    component: BrandStylesAddon,
  }),
);
